// src/components/PopupCard.jsx
import React, { useState, useEffect } from "react";
import { X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import PopupCarousel from "./PopupCarousel"; // Import the updated carousel

// -- Removed hardcoded slides data --

export default function PopupCard() {
  const [isOpen, setIsOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [popupData, setPopupData] = useState([]); // State to hold API data
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch popup data from the API
  useEffect(() => {
    const fetchPopups = async () => {
      setIsLoading(true);
      setError(null);
      // Reset data on refetch attempt if needed (optional)
      // setPopupData([]);
      // setActiveSlide(0);
      try {
        const response = await fetch(`${API_BASE_URL}/list-popup`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        // Filter for active popups and map to the structure needed
        const activePopups = data.popups
          .filter((popup) => popup.isActive)
          .map((popup) => ({
            id: popup.id, // Keep the ID
            image: popup.image,
            heading: popup.title, // Map title to heading
            catchphrase: popup.catchphrase,
            description: popup.description,
            // Add default CTA details
            ctaText: "Explore Now", // Changed default text slightly
            ctaLink: "/shop", // Default link
          }));

        if (activePopups.length > 0) {
          setPopupData(activePopups);
          // Check local storage *after* successfully fetching data
          const hasDismissed = localStorage.getItem("dontShowPopup");
          if (!hasDismissed) {
            setIsOpen(true);
            setActiveSlide(0); // Reset to first slide when opening
          }
        } else {
           setPopupData([]); // Ensure empty if no active popups
           setIsOpen(false); // Don't open if no data
          //  console.log("No active popups found.");
        }

      } catch (e) {
        console.error("Failed to fetch popups:", e);
        setError("Could not load promotional content.");
        setIsOpen(false); // Don't open on error
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if the popup hasn't been permanently dismissed
    const hasDismissed = localStorage.getItem("dontShowPopup");
    if (!hasDismissed) {
        fetchPopups();
    } else {
        setIsLoading(false); // Not loading if already dismissed
        setIsOpen(false);
    }

  }, []); // Empty dependency array: runs once on mount

  // Handle closing the popup
  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem("dontShowPopup", "true");
    }
    setIsOpen(false);
  };

  // Handle "Don't show again" checkbox change
  const handleCheckboxChange = (event) => {
    setDontShowAgain(event.target.checked);
  };

  // Animation variants
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 1, 0.5, 1] } }, // Smoother ease
    exit: { opacity: 0, scale: 0.95, y: 10, transition: { duration: 0.3, ease: "easeIn" } },
  };

  // Render nothing if not ready or dismissed
  if (isLoading || error || !isOpen || popupData.length === 0) {
    if (error) console.error("Popup Error:", error);
    // You could render a subtle loading indicator elsewhere if needed
    return null;
  }

  // Get the data for the currently active slide
  // Add safety check in case activeSlide becomes invalid temporarily
  const currentSlide = popupData[activeSlide] || popupData[0];
  if (!currentSlide) return null; // Extra safety


  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            // Increased max-width slightly, adjusted layout proportions
            className="relative w-full max-w-md md:max-w-3xl lg:max-w-4xl bg-gradient-to-br from-gray-50 via-white to-gray-100 rounded-xl shadow-xl overflow-hidden flex flex-col md:flex-row"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="popup-heading"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              aria-label="Close popup"
              className="absolute top-2.5 right-2.5 text-gray-500 hover:text-[#D4AF37] transition-colors duration-200 z-20 bg-white/80 rounded-full p-1.5 shadow-sm backdrop-blur-sm"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Left Side - Carousel */}
            {/* Adjusted width, added padding */}
            <div className="w-full md:w-[40%] lg:w-[45%] p-4 pb-6 md:p-6 flex items-center justify-center relative order-1 md:order-1">
              <div className="w-full max-w-[280px] md:max-w-xs lg:max-w-sm aspect-square">
                <PopupCarousel
                  slides={popupData}
                  onSlideChange={setActiveSlide} // Pass the state setter directly
                  dotStyle="custom-dots-gold" // Pass the class for styling dots
                />
              </div>
              {/* Custom styling for carousel dots - Using the passed class */}
              <style jsx global>{`
                .custom-dots-gold {
                  position: absolute;
                  bottom: -10px; /* Position below the carousel */
                  display: flex !important;
                  justify-content: center;
                  gap: 8px;
                  width: 100%;
                  padding: 0;
                  list-style: none; /* Ensure no list bullets */
                }
                .custom-dots-gold li {
                  width: 8px;
                  height: 8px;
                  margin: 0;
                  cursor: pointer;
                }
                .custom-dots-gold li button {
                  width: 100%;
                  height: 100%;
                  padding: 0;
                  border: none;
                  border-radius: 50%;
                  background-color: #ccc; /* Inactive dot color */
                  transition: background-color 0.3s ease;
                  display: block; /* Ensure button fills li */
                }
                .custom-dots-gold li button:before {
                  content: none !important; /* Remove default content */
                }
                .custom-dots-gold li.slick-active button {
                  background-color: #C19B2E; /* Active dot color (Gold) */
                }
                @media (min-width: 768px) {
                   .custom-dots-gold {
                     bottom: 0px; /* Adjust for larger screens */
                     gap: 10px;
                   }
                    .custom-dots-gold li {
                      width: 10px;
                      height: 10px;
                    }
                }
              `}</style>
            </div>

            {/* Right Side - Text Content */}
            {/* Adjusted width, padding, added min-height */}
            <div className="w-full md:w-[60%] lg:w-[55%] p-5 pt-2 md:p-8 flex flex-col justify-center order-2 md:order-2 bg-white/60 min-h-[250px]">
              {/* Heading */}
              <h2 id="popup-heading" className="text-xl md:text-2xl lg:text-3xl font-semibold text-[#333333] mb-1 md:mb-2 tracking-tight leading-tight">
                {currentSlide.heading}
              </h2>

              {/* Catchphrase */}
              <p className="text-sm md:text-base font-medium text-[#B08927] mb-3 md:mb-4 italic">
                {currentSlide.catchphrase}
              </p>

              {/* Description - handles newlines */}
              <p className="text-xs md:text-sm text-gray-700 mb-4 md:mb-6 whitespace-pre-line leading-relaxed flex-grow">
                {currentSlide.description}
              </p>

              {/* Bottom Section: CTA and Checkbox */}
              <div className="mt-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4 pt-2">
                {/* CTA Button */}
                <Link
                  to={currentSlide.ctaLink}
                  onClick={handleClose} // Close popup when CTA is clicked
                  className="group w-full sm:w-auto inline-flex items-center justify-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-white rounded-lg text-xs md:text-sm font-semibold uppercase tracking-wider
                  hover:from-[#C19B2E] hover:to-[#B08927] transition-all duration-300 ease-out
                  transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4AF37]/50 shadow-md hover:shadow-lg"
                >
                  <span>{currentSlide.ctaText}</span>
                  <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>

                {/* Don't Show Again Checkbox */}
                
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
